import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Target, Calendar, Archive, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CustomHabit {
  id: string;
  habit_name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  total_days: number;
  completed_days: number;
}

interface CustomHabitsProps {
  onBack: () => void;
}

export default function CustomHabits({ onBack }: CustomHabitsProps) {
  const [habits, setHabits] = useState<CustomHabit[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [newHabitName, setNewHabitName] = useState('');
  const [newHabitStartDate, setNewHabitStartDate] = useState('');
  const [newHabitEndDate, setNewHabitEndDate] = useState('');

  useEffect(() => {
    loadHabits();
  }, [showArchived]);

  const loadHabits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: habitsData, error } = await supabase
        .from('custom_habits')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', !showArchived)
        .order('created_at', { ascending: false });

      if (habitsData) {
        const habitsWithProgress = await Promise.all(
          habitsData.map(async (habit) => {
            const { count } = await supabase
              .from('custom_habit_completions')
              .select('*', { count: 'exact', head: true })
              .eq('habit_id', habit.id);

            const startDate = new Date(habit.start_date);
            const endDate = new Date(habit.end_date);
            const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

            return {
              ...habit,
              total_days: totalDays,
              completed_days: count || 0,
            };
          })
        );

        setHabits(habitsWithProgress);
      }
    } catch (error) {
      console.error('Error loading habits:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateHabit = async () => {
    if (!newHabitName.trim() || !newHabitStartDate || !newHabitEndDate) {
      alert('Please fill in all fields');
      return;
    }

    if (new Date(newHabitEndDate) < new Date(newHabitStartDate)) {
      alert('End date must be after start date');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('custom_habits').insert({
        user_id: user.id,
        habit_name: newHabitName.trim(),
        start_date: newHabitStartDate,
        end_date: newHabitEndDate,
        is_active: true,
      });

      setNewHabitName('');
      setNewHabitStartDate('');
      setNewHabitEndDate('');
      setShowCreateModal(false);
      loadHabits();
    } catch (error) {
      console.error('Error creating habit:', error);
      alert('Failed to create habit');
    }
  };

  const handleArchiveHabit = async (habitId: string) => {
    try {
      await supabase
        .from('custom_habits')
        .update({ is_active: false })
        .eq('id', habitId);

      loadHabits();
    } catch (error) {
      console.error('Error archiving habit:', error);
    }
  };

  const handleUnarchiveHabit = async (habitId: string) => {
    try {
      await supabase
        .from('custom_habits')
        .update({ is_active: true })
        .eq('id', habitId);

      loadHabits();
    } catch (error) {
      console.error('Error unarchiving habit:', error);
    }
  };

  const handleDeleteHabit = async (habitId: string) => {
    if (!confirm('Are you sure you want to delete this habit? This action cannot be undone.')) {
      return;
    }

    try {
      await supabase.from('custom_habits').delete().eq('id', habitId);
      loadHabits();
    } catch (error) {
      console.error('Error deleting habit:', error);
    }
  };

  const getProgressPercentage = (habit: CustomHabit) => {
    if (habit.total_days === 0) return 0;
    return Math.round((habit.completed_days / habit.total_days) * 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto pb-6">
        {/* Header */}
        <header className="bg-white px-6 py-4 flex items-center justify-between sticky top-0 z-10 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Custom Habits</h1>
              <p className="text-xs text-gray-500">Track your personal goals</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-10 h-10 rounded-full bg-gray-900 hover:bg-gray-800 flex items-center justify-center transition-colors"
          >
            <Plus className="w-5 h-5 text-white" strokeWidth={2.5} />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {/* Tab Switcher */}
          <div className="bg-white rounded-2xl border border-gray-200 p-1 flex">
            <button
              onClick={() => setShowArchived(false)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                !showArchived
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setShowArchived(true)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                showArchived
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Archived
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : habits.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">
                {showArchived ? 'No archived habits' : 'No habits yet'}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {showArchived
                  ? 'Archived habits will appear here'
                  : 'Create a habit to start tracking'}
              </p>
              {!showArchived && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-sm font-semibold transition-colors"
                >
                  Create Habit
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {habits.map((habit) => (
                <div
                  key={habit.id}
                  className="bg-white rounded-2xl p-4 border border-gray-200"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-base">
                        {habit.habit_name}
                      </h3>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                        <Calendar className="w-3 h-3" />
                        <span>
                          {new Date(habit.start_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                          {' - '}
                          {new Date(habit.end_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!showArchived ? (
                        <button
                          onClick={() => handleArchiveHabit(habit.id)}
                          className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                          title="Archive"
                        >
                          <Archive className="w-4 h-4 text-gray-400" />
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleUnarchiveHabit(habit.id)}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                            title="Restore"
                          >
                            <Archive className="w-4 h-4 text-cyan-500" />
                          </button>
                          <button
                            onClick={() => handleDeleteHabit(habit.id)}
                            className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500">Progress</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {habit.completed_days}/{habit.total_days} days
                      </span>
                      <span className="text-xs font-bold text-cyan-600">
                        {getProgressPercentage(habit)}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-cyan-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${getProgressPercentage(habit)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Create Habit</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Habit Name
                </label>
                <input
                  type="text"
                  value={newHabitName}
                  onChange={(e) => setNewHabitName(e.target.value)}
                  placeholder="e.g., Read 10 pages"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none transition-all text-gray-900"
                  maxLength={100}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newHabitStartDate}
                    onChange={(e) => setNewHabitStartDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none transition-all text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newHabitEndDate}
                    onChange={(e) => setNewHabitEndDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none transition-all text-gray-900"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewHabitName('');
                  setNewHabitStartDate('');
                  setNewHabitEndDate('');
                }}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateHabit}
                className="flex-1 px-4 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 font-semibold text-white transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}